// TMX (Tiled Map Editor) type definitions

/**
 * GID flip flags - upper 3 bits of tile GIDs encode flip state
 */
export const TMX_FLIP_H = 0x80000000 // bit 32 - horizontal flip
export const TMX_FLIP_V = 0x40000000 // bit 31 - vertical flip
export const TMX_FLIP_D = 0x20000000 // bit 30 - diagonal flip (rotate 90 + flip)
export const TMX_GID_MASK = 0x1fffffff // mask to extract actual GID

/**
 * Animation frame in a tileset
 */
export interface TMXAnimationFrame {
  tileId: number // local tile ID within the tileset
  duration: number // milliseconds
}

/**
 * Tile animation definition
 */
export interface TMXAnimation {
  frames: TMXAnimationFrame[]
  totalDuration: number // sum of all frame durations for cycling
}

/**
 * Tileset definition from TMX
 */
export interface TMXTileset {
  firstgid: number // first global ID for this tileset
  name: string
  tileWidth: number
  tileHeight: number
  tileCount: number
  columns: number
  image: string // path to tileset image
  imageWidth: number
  imageHeight: number
  animations: Record<number, TMXAnimation> // local tile ID -> animation
}

/**
 * Chunk of tile data (16x16 tiles typically)
 */
export interface TMXChunk {
  x: number // chunk position in tile coordinates
  y: number
  width: number // tiles per row (usually 16)
  height: number // tiles per column (usually 16)
  data: number[] // flattened GID array (row-major order)
}

/**
 * Map layer containing tile data
 */
export interface TMXLayer {
  id: number
  name: string
  offsetX: number // pixel offset
  offsetY: number
  visible: boolean
  opacity: number
  chunks: TMXChunk[]
}

/**
 * Point in a polyline or polygon
 */
export interface TMXPoint {
  x: number
  y: number
}

/**
 * Object in an object layer (for markers, paths, etc.)
 */
export interface TMXObject {
  id: number
  name: string
  type: string // object type/class
  x: number // position in pixels (TMX coordinates)
  y: number
  width?: number
  height?: number
  polyline?: TMXPoint[] // for path objects
  polygon?: TMXPoint[] // for area objects
}

/**
 * Object layer containing game markers, paths, etc.
 */
export interface TMXObjectGroup {
  id: number
  name: string
  visible: boolean
  opacity: number
  offsetX: number
  offsetY: number
  objects: TMXObject[]
}

/**
 * Complete TMX map definition
 */
export interface TMXMap {
  version: string
  orientation: "orthogonal" | "isometric" | "staggered" | "hexagonal"
  renderOrder: "right-down" | "right-up" | "left-down" | "left-up"
  width: number // map width in tiles
  height: number // map height in tiles
  tileWidth: number // tile size in pixels
  tileHeight: number
  infinite: boolean
  tilesets: TMXTileset[]
  layers: TMXLayer[]
  objectGroups?: TMXObjectGroup[] // optional object layers for path, markers, etc.
}

/**
 * Extracted level data from TMX object layers
 */
export interface TMXLevelData {
  path?: TMXPoint[] // enemy path waypoints (converted to game coordinates)
  spawnPoint?: TMXPoint // where enemies spawn
  basePosition?: TMXPoint // where the base is
  landingTile?: { x: number; y: number } // droppod landing position (tile coords)
}

/**
 * Resolved tile data after parsing GID
 */
export interface TMXTileData {
  tileset: TMXTileset
  localId: number // tile ID within the tileset
  srcX: number // source X in tileset image (pixels)
  srcY: number // source Y in tileset image (pixels)
  flipH: boolean
  flipV: boolean
  flipD: boolean
}

/**
 * Viewport for rendering
 */
export interface TMXViewport {
  x: number
  y: number
  width: number
  height: number
}
